import { useMemo } from "react";
import { useFeatureFlagJsonIgnoreVariant } from "@apac/feature-flags";
import type { Traveler } from "@b2bportal/traveler-api";
import { useCheckoutStateSelector } from "@hopper-b2b/checkout";
import { FeatureFlagNames } from "@hsbc/utilities";
import { getSelectedTripDetails } from "../selectors/common";

export type AirTravelerFormExceptionField =
  | "gender"
  | "nationality"
  | "passport";

/**
 * JSON payload shape for the `hsbc<tenant>-air-traveler-form-exception`
 * feature flag. The map is keyed by IATA airline code; if any segment in the
 * selected itinerary uses an airline listed here, the matching fields are
 * shown and required on the traveler form.
 */
export type AirTravelerFormExceptionFlag = {
  airlines?: Record<
    string,
    // Explicit `| undefined` is load-bearing: without `noUncheckedIndexedAccess`,
    // `Record<string, T>` treats absent keys as `T` rather than `T | undefined`,
    // which would let the lint rule flag the necessary runtime `if (!overrides)`
    // guard below as always-true. JSON payloads from the flag service can also
    // contain explicit nulls for individual airline keys.
    Partial<Record<AirTravelerFormExceptionField, boolean>> | undefined
  >;
};

export type AirTravelerFormRequiredFields = Record<
  AirTravelerFormExceptionField,
  boolean
>;

const EMPTY_REQUIRED_FIELDS: AirTravelerFormRequiredFields = {
  gender: false,
  nationality: false,
  passport: false,
};

const getSelectedTripAirlineCodes = (
  tripDetails: ReturnType<typeof getSelectedTripDetails>
): string[] => {
  const codes = new Set<string>();
  tripDetails?.slices?.forEach((slice) => {
    slice?.segmentDetails?.forEach((segment) => {
      const code = segment?.marketingAirline?.code;
      if (code) codes.add(code);
    });
  });
  return Array.from(codes);
};

/**
 * Returns the union of the per-airline `gender`/`nationality`/`passport`
 * requirements from the active tenant's air-traveler-form-exception flag,
 * across every airline that appears in any segment of the selected trip.
 *
 * Returns all-false when:
 *  - The flag is not set for the current tenant (e.g. apps other than
 *    hsbc-in, which is the only tenant wired for now).
 *  - No trip is selected yet.
 *  - No airline in the itinerary matches an entry in the flag's `airlines`
 *    map.
 */
export const useAirTravelerFormRequiredFields =
  (): AirTravelerFormRequiredFields => {
    const flag = useFeatureFlagJsonIgnoreVariant<AirTravelerFormExceptionFlag>(
      FeatureFlagNames.AIR_TRAVELER_FORM_EXCEPTION_HSBC_IN
    );
    const tripDetails = useCheckoutStateSelector(getSelectedTripDetails);

    return useMemo(() => {
      if (!flag?.airlines) return EMPTY_REQUIRED_FIELDS;
      const airlineCodes = getSelectedTripAirlineCodes(tripDetails);
      if (airlineCodes.length === 0) return EMPTY_REQUIRED_FIELDS;

      const result: AirTravelerFormRequiredFields = {
        ...EMPTY_REQUIRED_FIELDS,
      };
      for (const code of airlineCodes) {
        const overrides = flag.airlines[code];
        if (!overrides) continue;
        if (overrides.gender) result.gender = true;
        if (overrides.nationality) result.nationality = true;
        if (overrides.passport) result.passport = true;
      }
      return result;
    }, [flag, tripDetails]);
  };

/**
 * Returns true when the traveler has all the data required by the given
 * `required` shape. Mirrors the field paths used by the traveler form: gender
 * lives at `airTravelerDetails.gender`, nationality at
 * `airTravelerDetails.nationality.country`, and passport requires all three
 * of number, expiration, and country of issue.
 */
export const isAirTravelerComplete = (
  traveler: Traveler | undefined,
  required: AirTravelerFormRequiredFields
): boolean => {
  if (!traveler) return false;
  const details = traveler.airTravelerDetails;

  if (required.gender && !details?.gender) return false;
  if (required.nationality && !details?.nationality?.country) return false;
  if (required.passport) {
    const passport = details?.passport;
    if (
      !passport?.number ||
      !passport?.expiration ||
      !passport?.countryOfIssue
    ) {
      return false;
    }
  }
  return true;
};

/**
 * Returns the subset of `AirTravelerFormExceptionField`s where the traveler
 * is missing data, scoped to the fields the caller actually requires. Useful
 * for deciding which "missing info" banner to show on the traveler form.
 */
export const getMissingAirTravelerFields = (
  traveler: Traveler | undefined,
  required: AirTravelerFormRequiredFields
): AirTravelerFormRequiredFields => {
  if (!traveler) return required;
  const details = traveler.airTravelerDetails;
  return {
    gender: required.gender && !details?.gender,
    nationality: required.nationality && !details?.nationality?.country,
    passport:
      required.passport &&
      (!details?.passport?.number ||
        !details?.passport?.expiration ||
        !details?.passport?.countryOfIssue),
  };
};
